import { useShowNotification } from '@inflearn/ds-react-next';
import _ from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { Suspense, useEffect, useState } from 'react';

import useClientAccountNotificationUpdateMutation from '../../../../../mutations/useClientAccountNotificationUpdateMutation';
import useClientAccountNotificationQuery from '../../../../../queries/useClientAccountNotificationQuery';
import { DeferredComponent } from '../../../../util/DeferredComponent';
import NotificationSetting from './NotificationSetting';
import { NotificationSkeleton } from './NotificationSkeleton';
import { type Type, initialNotificationStatus, notificationTypeMapper } from './constants';

export default function NotificationSettingPageComponent() {
  return (
    <Suspense
      fallback={
        <DeferredComponent>
          <NotificationSkeleton />
        </DeferredComponent>
      }
    >
      <_NotificationSettingPageComponent />
    </Suspense>
  );
}

function _NotificationSettingPageComponent() {
  const { t } = useTranslation();
  const { showNotification } = useShowNotification();
  const [notificationStatus, setNotificationStatus] = useState(initialNotificationStatus);

  const {
    data: notificationData,
    isLoading,
    isError,
  } = useClientAccountNotificationQuery({
    suspense: true,
    useErrorBoundary: false,
  });

  const { mutateAsync } = useClientAccountNotificationUpdateMutation({
    onSuccess: () => {
      showNotification({
        title: t('알림 설정을 변경했습니다.'),
        type: 'success',
      });
    },
    onError: () => {
      showNotification({
        title: t('알림 설정을 변경하는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'),
        type: 'error',
        message: '',
      });
    },
  });

  const updateNotificationStatus = async (type: Type, status: boolean) => {
    setNotificationStatus((prev) => ({
      ...prev,
      [type]: status,
    }));
    mutateAsync({
      type,
      status,
    });
  };

  useEffect(() => {
    if (!isError) {
      return;
    }

    showNotification({
      title: t('알림 설정을 불러오는 중 오류가 발생했습니다.'),
      type: 'error',
    });
  }, [isError]);

  useEffect(() => {
    if (_.isNil(notificationData)) {
      return;
    }

    const result: Record<Type, boolean> = { ...initialNotificationStatus };

    Object.entries(notificationData).forEach(([key, value]) => {
      result[notificationTypeMapper[key as keyof typeof notificationTypeMapper]] = Boolean(value);
    });

    setNotificationStatus(result);
  }, [notificationData]);

  return (
    <NotificationSetting
      updateNotificationStatus={updateNotificationStatus}
      notificationStatus={notificationStatus}
      loading={isLoading}
    />
  );
}
